import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  Stethoscope, 
  Clock, 
  ShieldCheck, 
  ArrowRight,
  Activity,
  Home,
  Building2,
  AlertTriangle
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSelector } from "./LanguageSelector";

interface TriageIntroModalProps {
  open: boolean;
  onStart: () => void;
  onSkip: () => void;
}

export default function TriageIntroModal({ open, onStart, onSkip }: TriageIntroModalProps) {
  const { t } = useLanguage();

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-lg animate-scale-in" 
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Language Selector */}
        <div className="absolute top-4 right-12 z-10">
          <LanguageSelector />
        </div>

        <DialogHeader className="text-center space-y-4 pt-8">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center animate-fade-in">
            <Stethoscope className="h-8 w-8 text-primary-foreground animate-pulse" />
          </div>
          <DialogTitle className="text-2xl animate-fade-in [animation-delay:100ms]">
            {t.intro.title}
          </DialogTitle>
          
          {/* Multi-language translations */}
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 text-muted-foreground text-xl">
            <span className="font-hindi">एआई लक्षण जांचकर्ता</span>
            <span className="text-muted-foreground/50">•</span>
            <span className="font-punjabi">ਏਆਈ ਲੱਛਣ ਜਾਂਚਕਰਤਾ</span>
            <span className="text-muted-foreground/50">•</span>
            <span className="font-bengali">এআই লক্ষণ পরীক্ষক</span>
          </div>
          
          <DialogDescription className="text-base animate-fade-in [animation-delay:150ms]">
            {t.intro.subtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* How it works */}
          <div className="space-y-3 animate-fade-in [animation-delay:200ms]">
            <h4 className="font-semibold text-foreground text-sm">{t.intro.howItWorks}</h4>
            <div className="flex flex-wrap gap-x-2 gap-y-1 text-muted-foreground text-base">
              <span className="font-hindi">यह कैसे काम करता है</span>
              <span className="text-muted-foreground/50">•</span>
              <span className="font-punjabi">ਇਹ ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ</span>
              <span className="text-muted-foreground/50">•</span>
              <span className="font-bengali">এটি কিভাবে কাজ করে</span>
            </div>
            <div className="grid gap-3">
              <div className="flex items-start gap-3 text-sm animate-fade-in [animation-delay:250ms]">
                <div className="p-1.5 rounded-lg bg-primary/10 shrink-0 transition-transform hover:scale-110">
                  <Activity className="h-4 w-4 text-primary" />
                </div>
                <p className="text-muted-foreground">
                  {t.intro.step1}
                </p>
              </div>
              <div className="flex items-start gap-3 text-sm animate-fade-in [animation-delay:300ms]">
                <div className="p-1.5 rounded-lg bg-primary/10 shrink-0 transition-transform hover:scale-110">
                  <Clock className="h-4 w-4 text-primary" />
                </div>
                <p className="text-muted-foreground">
                  {t.intro.step2}
                </p>
              </div>
              <div className="flex items-start gap-3 text-sm animate-fade-in [animation-delay:350ms]">
                <div className="p-1.5 rounded-lg bg-primary/10 shrink-0 transition-transform hover:scale-110">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                </div>
                <p className="text-muted-foreground">
                  {t.intro.step3}
                </p>
              </div>
            </div>
          </div>

          {/* Possible recommendations */}
          <div className="space-y-3 animate-fade-in [animation-delay:400ms]">
            <h4 className="font-semibold text-foreground text-sm">{t.intro.guidance}</h4>
            <div className="flex flex-wrap gap-x-2 gap-y-1 text-muted-foreground text-base">
              <span className="font-hindi">आपको मार्गदर्शन मिलेगा</span>
              <span className="text-muted-foreground/50">•</span>
              <span className="font-punjabi">ਤੁਹਾਨੂੰ ਮਾਰਗਦਰਸ਼ਨ ਮਿਲੇਗਾ</span>
              <span className="text-muted-foreground/50">•</span>
              <span className="font-bengali">আপনি নির্দেশনা পাবেন</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-xs transition-all hover:scale-105 hover:shadow-md animate-fade-in [animation-delay:450ms]">
                <Home className="h-3.5 w-3.5" />
                <span>{t.intro.homeCare}</span>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-500/10 text-blue-700 dark:text-blue-400 text-xs transition-all hover:scale-105 hover:shadow-md animate-fade-in [animation-delay:500ms]">
                <Stethoscope className="h-3.5 w-3.5" />
                <span>{t.intro.seeDoctor}</span>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs transition-all hover:scale-105 hover:shadow-md animate-fade-in [animation-delay:550ms]">
                <Building2 className="h-3.5 w-3.5" />
                <span>{t.intro.urgentCare}</span>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-red-500/10 text-red-700 dark:text-red-400 text-xs transition-all hover:scale-105 hover:shadow-md animate-fade-in [animation-delay:600ms]">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span>{t.intro.emergencyRoom}</span>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="text-xs text-muted-foreground text-center bg-muted/50 p-3 rounded-lg animate-fade-in [animation-delay:650ms]">
            {t.intro.disclaimer}
          </p>
        </div>

        <div className="space-y-3 animate-fade-in [animation-delay:700ms]">
          <Button 
            onClick={onStart} 
            size="lg" 
            className="w-full hover:scale-[1.02] transition-transform"
          >
            {t.intro.startAssessment}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          
          <button
            onClick={onSkip}
            className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
          >
            {t.intro.skipIntro}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}